import type {
  ColourableElement,
  DocTheme,
  FontFamilyId,
  HeadingLevel,
  ThemeOverrides,
} from "./types";
import { COLOURABLE_ELEMENTS, HEADING_LEVELS } from "./types";
import { FONT_STACKS, getPreset, PRESETS, DEFAULT_PRESET_ID } from "./presets";

/**
 * Bounds for every numeric knob. Used both to clamp computed values and to
 * validate inbound payloads (defence against CSS injection / absurd inputs).
 */
export const LIMITS = {
  baseFontSize: { min: 10, max: 32 }, // px
  fontScale: { min: 1.0, max: 1.6 }, // modular-scale ratio
  spacing: { min: 0.5, max: 2.0 }, // multiplier
  weight: { min: 100, max: 900 },
} as const;

/** Base spacing unit (em) that the spacing multiplier scales. */
const SPACE_BASE_EM = 0.75;

/** Heading step offsets above body. h1 is the largest; h6 sits just above body. */
// Step offsets above body for each heading level. Compressed at the top so a
// document h1 lands around 2× body at a typical 1.2 ratio (1.2^4 ≈ 2.07) —
// matching Word/Google-Docs proportions — rather than 1.2^6 ≈ 3× which reads
// like a marketing display heading. h4–h6 stay subtle and close to body.
const HEADING_STEPS: Record<"h1" | "h2" | "h3" | "h4" | "h5" | "h6", number> = {
  h1: 4,
  h2: 3,
  h3: 2.2,
  h4: 1.4,
  h5: 0.8,
  h6: 0.4,
};

/** Line-height ratio applied to each computed size. Looser for body. */
const LEADING_RATIO_BODY = 1.5;
const LEADING_RATIO_HEADING = 1.2;

function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

function round(n: number, dp = 4): number {
  const f = 10 ** dp;
  return Math.round(n * f) / f;
}

/** Resolve a font-family id to its CSS stack; unknown ids fall back to sans. */
function fontStack(id: FontFamilyId | undefined): string {
  return (id && FONT_STACKS[id]) || FONT_STACKS.sans;
}

/**
 * Reject anything that doesn't look like a safe CSS color. We accept:
 *  - CSS custom-property refs we control (`var(--primary)` etc.)
 *  - hex, rgb()/rgba(), hsl()/hsla(), oklch()/oklab(), and named colors.
 * Anything with characters that could break out of an inline `style` (`;`, `{`,
 * `}`, `<`, `>`, url(), expression()) is rejected → caller drops the override.
 */
const SAFE_COLOR = /^(?:var\(--[a-z0-9-]+\)|#[0-9a-f]{3,8}|(?:rgb|rgba|hsl|hsla|oklch|oklab)\([0-9.,%/\sa-z-]+\)|[a-z]+)$/i;

export function isSafeColor(value: unknown): value is string {
  return typeof value === "string" && value.length <= 64 && SAFE_COLOR.test(value.trim());
}

/**
 * Normalise/validate a raw overrides bag from an untrusted source. Out-of-range
 * numbers are clamped, unsafe colours and unknown font ids dropped. Returns a
 * sparse object containing only the valid fields.
 */
export function sanitizeOverrides(raw: unknown): ThemeOverrides {
  if (!raw || typeof raw !== "object") return {};
  const o = raw as Record<string, unknown>;
  const out: ThemeOverrides = {};

  if (isSafeColor(o.accent)) out.accent = o.accent.trim();
  if (isSafeColor(o.bodyColor)) out.bodyColor = o.bodyColor.trim();
  if (isSafeColor(o.background)) out.background = o.background.trim();
  if (isSafeColor(o.headingColor)) out.headingColor = o.headingColor.trim();

  // Levels/elements are enum-ish: keep only recognised members, dedupe, and
  // drop the field entirely when nothing valid survives so the default (all
  // levels / no extra elements) applies rather than "an empty selection".
  if (Array.isArray(o.headingColorLevels)) {
    const levels = HEADING_LEVELS.filter((l) =>
      (o.headingColorLevels as unknown[]).includes(l),
    );
    if (levels.length) out.headingColorLevels = levels;
  }
  if (Array.isArray(o.headingColorElements)) {
    const els = COLOURABLE_ELEMENTS.filter((e) =>
      (o.headingColorElements as unknown[]).includes(e),
    );
    if (els.length) out.headingColorElements = els;
  }

  for (const key of ["fontBody", "fontHeading", "fontMono"] as const) {
    const v = o[key];
    if (typeof v === "string" && v in FONT_STACKS) out[key] = v as FontFamilyId;
  }

  if (typeof o.bodyWeight === "number")
    out.bodyWeight = clamp(Math.round(o.bodyWeight), LIMITS.weight.min, LIMITS.weight.max);
  if (typeof o.headingWeight === "number")
    out.headingWeight = clamp(Math.round(o.headingWeight), LIMITS.weight.min, LIMITS.weight.max);
  if (typeof o.italicHeadings === "boolean") out.italicHeadings = o.italicHeadings;

  if (typeof o.baseFontSize === "number")
    out.baseFontSize = clamp(o.baseFontSize, LIMITS.baseFontSize.min, LIMITS.baseFontSize.max);
  if (typeof o.fontScale === "number")
    out.fontScale = clamp(o.fontScale, LIMITS.fontScale.min, LIMITS.fontScale.max);
  if (typeof o.spacing === "number")
    out.spacing = clamp(o.spacing, LIMITS.spacing.min, LIMITS.spacing.max);

  return out;
}

/** Validate/normalise an untrusted DocTheme payload (for API ingress). */
export function sanitizeTheme(raw: unknown): DocTheme {
  if (!raw || typeof raw !== "object") return { preset: DEFAULT_PRESET_ID };
  const r = raw as Record<string, unknown>;
  const preset = typeof r.preset === "string" && r.preset in PRESETS ? r.preset : DEFAULT_PRESET_ID;
  return { preset, overrides: sanitizeOverrides(r.overrides) };
}

/**
 * Resolve the effective theme: per-doc override wins, else the user default,
 * else the built-in default preset. Each layer is independently sanitised.
 */
export function resolveTheme(
  perDoc?: DocTheme | null,
  userDefault?: DocTheme | null,
): DocTheme {
  if (perDoc) return sanitizeTheme(perDoc);
  if (userDefault) return sanitizeTheme(userDefault);
  return { preset: DEFAULT_PRESET_ID };
}

/**
 * Compute the flat CSS-variable map for a theme. The preset supplies a complete
 * baseline; overrides (already sanitised here) layer on top. Font sizes are
 * derived from base × scale^step so a single knob moves the whole hierarchy.
 *
 * Keys returned are exactly those consumed by `@workspace/markdown/doc-typography.css`.
 */
export function themeToCssVars(theme: DocTheme): Record<string, string> {
  const preset = getPreset(theme.preset);
  const overrides = sanitizeOverrides(theme.overrides);
  const o = { ...preset.base, ...overrides };

  const base = o.baseFontSize; // px
  const scale = o.fontScale;
  const space = round(SPACE_BASE_EM * o.spacing, 4);

  // A preset may pin an explicit (possibly irregular) size table — honoured
  // verbatim until the user touches either sizing knob, at which point we
  // switch to the derived modular scale so one knob moves everything.
  const sizingTouched =
    overrides.baseFontSize !== undefined || overrides.fontScale !== undefined;
  const fixed = !sizingTouched ? preset.fixedSizesRem : undefined;

  // Mix operands are the RESOLVED values, not `var(--doc-foreground)` — those
  // custom properties are declared in this same block, and a var() reference to
  // a sibling declaration in one inline `style` attribute does resolve, but
  // keeping it explicit avoids depending on that ordering subtlety.
  const MIX_FG = o.bodyColor;
  const MIX_BG = o.background;
  const MIX_ACCENT = o.accent;

  const vars: Record<string, string> = {
    // Colours.
    //
    // Deliberately namespaced `--doc-*` rather than reusing the app's own
    // `--primary` / `--foreground` / `--background`. The neutral presets set
    // these to `var(--primary)` etc. so they track the app chrome — and a custom
    // property defined in terms of ITSELF on the same element is a cycle, which
    // CSS resolves by throwing the declaration away. That is survivable for
    // `color` (it falls back to inheritance) but not for a background or a
    // `color-mix()`, which land on transparent.
    "--doc-accent": o.accent,
    "--doc-foreground": o.bodyColor,
    "--doc-background": o.background,
    // Derived secondary colours. These exist because the stylesheet used to
    // reach for the APP's `--muted-foreground` / `--muted` / `--border` for
    // blockquotes, code fills, table headers and rules — tokens the theme never
    // touches. On a themed page that left them off-palette (app grey on cream
    // paper), and in dark mode actively wrong: a light-grey muted token on a
    // light themed surface. Mixing from the document's own ink and surface
    // keeps them in step with whatever the theme resolves to.
    "--doc-muted-foreground": `color-mix(in oklch, ${MIX_FG}, ${MIX_BG} 45%)`,
    "--doc-muted": `color-mix(in oklch, ${MIX_FG}, ${MIX_BG} 94%)`,
    "--doc-border": `color-mix(in oklch, ${MIX_FG}, ${MIX_BG} 82%)`,
    // Highlight: the accent hue laid over the document's own surface, so it
    // stays legible on a dark page without keying off the app's `.dark` class
    // (which describes the chrome, not this document).
    "--doc-highlight": `color-mix(in oklch, ${MIX_ACCENT}, ${MIX_BG} 72%)`,
    // Fonts — namespaced for the same reason as the colours above: `sans` maps
    // to `var(--font-sans)`, so emitting it under that name would be a cycle.
    "--doc-font-body": fontStack(o.fontBody),
    "--doc-font-heading": fontStack(o.fontHeading ?? o.fontBody),
    "--doc-font-mono": fontStack(o.fontMono),
    // Weights / style
    "--doc-body-weight": String(o.bodyWeight),
    "--doc-heading-weight": String(o.headingWeight),
    "--doc-heading-style": o.italicHeadings ? "italic" : "normal",
    // Spacing
    "--doc-space": `${space}em`,
  };

  // Body size — fixed table (rem) or derived from base px.
  const bodyRem = fixed ? fixed.body : round(base / 16, 4);
  vars["--text-body"] = `${bodyRem}rem`;
  vars["--leading-body"] = `${round(bodyRem * LEADING_RATIO_BODY, 4)}rem`;
  vars["--text-body-sm"] = `${round(bodyRem * 0.875, 4)}rem`;
  vars["--leading-body-sm"] = `${round(bodyRem * 0.875 * LEADING_RATIO_BODY, 4)}rem`;

  // Heading sizes — fixed table or modular scale (base × scale^step) above body.
  for (const [tag, step] of Object.entries(HEADING_STEPS)) {
    const rem = fixed
      ? fixed[tag as keyof typeof fixed]
      : round((base * scale ** step) / 16, 4);
    vars[`--text-${tag}`] = `${rem}rem`;
    vars[`--leading-${tag}`] = `${round(rem * LEADING_RATIO_HEADING, 4)}rem`;
  }

  // Heading colour. Unset → headings keep body ink, which is how every preset
  // ships, so this whole block is a no-op by default.
  //
  // Emitted PER LEVEL (`--doc-h1-color` …) rather than as one var plus a
  // selector list, because the set of coloured levels is data, not structure:
  // CSS cannot build a selector from a custom property, and per-level vars let
  // the stylesheet stay a fixed six rules while the theme decides which
  // resolve to the heading colour and which fall back to body ink.
  const headingColor = o.headingColor;
  if (headingColor) {
    const levels: HeadingLevel[] = o.headingColorLevels ?? HEADING_LEVELS;
    for (const level of HEADING_LEVELS) {
      if (levels.includes(level)) {
        vars[`--doc-h${level}-color`] = headingColor;
      }
    }
    const elements: ColourableElement[] = o.headingColorElements ?? [];
    for (const el of COLOURABLE_ELEMENTS) {
      if (elements.includes(el)) {
        vars[`--doc-${el}-color`] = headingColor;
      }
    }
  }

  return vars;
}
